import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import React, { useContext } from "react";
import Flex from "common/Boxes/Flex";
import { DomainIcon, SecurityIcon, TwoFAIcon } from "common/Icons/Icons";
import styled, { useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";
import { Globe, KeyRound, LockKeyhole, ShieldCheck } from "lucide-react";
import useMedia from "utils/hooks/useMedia";
import { LARGE_MOBILE } from "common/constants";
import { ExternalConnectAccountContext } from "@shared/features/configuration/ExternalConnectAccount.context.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { useLocation } from "react-router-dom";
import { AUTH_SECURITY_SECTION_ID, AUTH_SECURITY_TIP_ICON_ID } from "testIds";

const SecuritySection = () => {
  const theme = useTheme();
  const isMobile = useMedia([`(max-width: ${LARGE_MOBILE})`], [true], false);
  const { isExternalConnectAccount } =
    useContext(ExternalConnectAccountContext) || {};
  const { brandingData } = useBranding();
  const { t } = useTypedTranslation(LanguageNameSpaces.AUTH);
  const isRegister = useLocation().pathname.startsWith("/register");

  const iconStyle = {
    width: "100%",
    height: "100%",
    fill: theme.colors.primaryGrey,
  };

  if (theme.isNewLook && !isExternalConnectAccount) {
    const domain = brandingData?.cname ?? "dashboard.cornix.io";
    // Emphasise the domain itself: it is the one thing to compare.
    const [beforeDomain, afterDomain = ""] = t("security.domain", {
      domain: DOMAIN_SLOT,
    }).split(DOMAIN_SLOT);
    const iconProps = {
      size: 14,
      strokeWidth: 1.75,
      "aria-hidden": true,
      "data-testid": AUTH_SECURITY_TIP_ICON_ID,
    };
    return (
      <Checklist data-testid={AUTH_SECURITY_SECTION_ID}>
        <ChecklistTitle>
          <ShieldCheck size={14} strokeWidth={1.75} aria-hidden />
          {t("security.title")}
        </ChecklistTitle>
        <ChecklistItems>
          <li>
            <Globe {...iconProps} />
            <span>
              {beforeDomain}
              <Domain>{domain}</Domain>
              {afterDomain}
            </span>
          </li>
          <li>
            <LockKeyhole {...iconProps} />
            <span>{t("security.https")}</span>
          </li>
          <li>
            <KeyRound {...iconProps} />
            <span>
              {t(isRegister ? "security.setup_two_fa" : "security.two_fa")}
            </span>
          </li>
        </ChecklistItems>
      </Checklist>
    );
  }

  return (
    <Section
      data-testid={AUTH_SECURITY_SECTION_ID}
      $flexDirection="column"
      style={{
        marginBottom: isExternalConnectAccount ? 0 : isMobile ? 32 : 70,
      }}
    >
      <Title level={3}>{t("security.title")}</Title>
      <ItemsWrapper
        $isMobile={isMobile}
        $flexDirection={isMobile ? "column" : "row"}
        $alignItems={isMobile ? "flex-start" : "center"}
      >
        <SecurityItem>
          <IconBg>
            <DomainIcon style={iconStyle} />
          </IconBg>
          <StyledText>
            {t("security.domain", {
              domain: brandingData?.cname ?? "dashboard.cornix.io",
            })}
          </StyledText>
        </SecurityItem>
        <SecurityItem>
          <IconBg>
            <SecurityIcon style={iconStyle} />
          </IconBg>
          <StyledText>{t("security.https")}</StyledText>
        </SecurityItem>
        <SecurityItem>
          <IconBg>
            <TwoFAIcon style={iconStyle} />
          </IconBg>
          <StyledText>
            {t(isRegister ? "security.setup_two_fa" : "security.two_fa")}
          </StyledText>
        </SecurityItem>
      </ItemsWrapper>
    </Section>
  );
};

export default SecuritySection;

const ICON_SIZE = 48;
const MOBILE_ICON_SIZE = 42;

const Section = styled(Flex)`
  width: 100%;
  max-width: 700px;
  @media (max-width: ${LARGE_MOBILE}) {
    box-sizing: border-box;
    max-width: 480px;
    padding: 0 20px;
  }
`;
const SecurityItem = styled(Flex)`
  flex: 1;
  min-width: 0;
  @media (max-width: ${LARGE_MOBILE}) {
    width: 100%;
  }
`;
const ItemsWrapper = styled(Flex)<{ $isMobile?: boolean }>`
  margin-top: 24px;
  max-width: 700px;
  width: 100%;
  column-gap: ${(p) => (p.$isMobile ? 12 : 16)}px;
  row-gap: 20px;
`;
const StyledText = styled(Text).attrs({ type: "secondary" })`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;
const IconBg = styled.div`
  border-radius: 50px;
  background: ${(p) => p.theme.colors.lightBlue};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: ${ICON_SIZE}px;
  height: ${ICON_SIZE}px;
  padding: 10px;
  margin-right: 16px;
  @media (max-width: ${LARGE_MOBILE}) {
    width: ${MOBILE_ICON_SIZE}px;
    height: ${MOBILE_ICON_SIZE}px;
    padding: 8px;
    margin-right: 12px;
  }
`;

const DOMAIN_SLOT = "\u0000";

// New look: the form panel's footer (the layout gives it the well and the
// hairline above), so the checklist itself is just type.
const Checklist = styled.section`
  box-sizing: border-box;
  width: 100%;
  text-align: start;
`;
const ChecklistTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  ${newLookType.label};
  color: ${(p) => p.theme.colors.secondaryText};
  svg {
    flex: none;
    color: ${(p) => p.theme.colors.secondaryText};
  }
`;
const ChecklistItems = styled.ul`
  display: grid;
  gap: 6px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
  li {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    column-gap: 8px;
    align-items: start;
    ${newLookType.meta};
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
  li svg {
    color: ${(p) => getNewLookTokens(p.theme).muted};
  }
  li span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;
const Domain = styled.strong`
  color: ${(p) => p.theme.colors.titleColor};
  font-weight: 500;
`;
